/* sts-data-layer.js — window.STS.profile, the one profile read model for v4.
 *
 * Spec: docs/new-theme-v4/specs/01-data-model-backend-spec.md §3.12 (row 01-2),
 *       burn-down slice S13. First consumer: the liked-brand and over-budget card
 *       flags (spec 04, row 04-W64), drawn by assets/sts-collection.js.
 *
 * Where the profile comes from
 *   signed in  the JSON block snippets/sts-customer-data.liquid prints from the
 *              customer's metafields (source 'liquid'). The account wins wherever it
 *              has a value. Liked brands fall back to this device's saved list only
 *              while the account has none, the same merge rule the Skincare Library
 *              uses for topics (decision 07-Q21). sts-logout.js clears that device
 *              list at sign-out, so it cannot follow a shared device to the next person.
 *   guest      what the head resolver already put on STS.state (type, typedAt), plus
 *              the brands in localStorage['sts.profile.guest'] if this device saved any
 *              (source 'local'). A guest has no budget: the over-budget flag is for
 *              signed-in shoppers with a budget only (decision 04-Q19).
 *
 * The shape. Every field is always present.
 *   STS.profile = { v, source, loggedIn, type, typedAt, likedBrands, budget, budgetCents,
 *                   currency, shopCurrency }
 *     likedBrands  vendor handles in lower case. Compare them with a card's data-sts-brand.
 *     budgetCents  the whole-routine cap in cents; 0 means none. A single product priced
 *                  above it is over budget however the routine is split.
 *
 * It never clobbers. window.STS is the object the head resolver created and
 * assets/sts-state.js extended, and this file only adds to it. If something already
 * published STS.profile (the Skin Profile's assets/sts-personalize.js carries the live,
 * editable copy), that object's fields win and this file only fills the missing ones. It
 * fills them again whenever that page republishes (sts:profile:change).
 *
 * Readiness
 *   STS.profile is set as soon as this file runs. `sts:profile:ready` fires on document
 *   at DOMContentLoaded, after every deferred script has run, so a deferred consumer can
 *   just listen for it. Code that may run later calls STS.onProfile(fn), which calls fn
 *   at once if the event has already fired and again on every sts:profile:change.
 *
 * Nothing here goes to window.dataLayer or any pixel. Skin type is marked no-pixel until
 * 09-Q11 is answered.
 */
(function (w, d) {
  'use strict';

  var STS = (w.STS = w.STS || {});
  var TYPES = STS.TYPES || ['DSPT', 'DSNT', 'DSPW', 'DSNW', 'OSPT', 'OSNT', 'OSPW', 'OSNW',
    'ORPT', 'ORNT', 'ORPW', 'ORNW', 'DRPT', 'DRNT', 'DRPW', 'DRNW'];
  var GUEST_KEY = 'sts.profile.guest';
  var MAX_BRANDS = 50;

  // ------------------------------------------------------------------ util

  /* Safari private browsing throws on localStorage READ, not only on write. */
  function lsGet(key) {
    try { return w.localStorage.getItem(key); } catch (e) { return null; }
  }
  function parse(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  function typeCode(t) {
    t = String(t || '').trim().toUpperCase();
    return TYPES.indexOf(t) > -1 ? t : null;
  }

  function handles(list) {
    var out = [];
    if (!list || typeof list === 'string' || typeof list.length !== 'number') return out;
    for (var i = 0; i < list.length && out.length < MAX_BRANDS; i++) {
      var h = String(list[i] == null ? '' : list[i]).trim().toLowerCase();
      if (h && out.indexOf(h) === -1) out.push(h);
    }
    return out;
  }

  /* Epoch ms from the Liquid block, or an ISO-ish string from STS.state. */
  function iso(v) {
    if (v == null || v === '') return null;
    var t = typeof v === 'number' ? v : Date.parse(v);
    return isFinite(t) && t > 0 ? new Date(t).toISOString() : null;
  }

  // ----------------------------------------------------------------- build

  /* The buyer's presentment currency and the shop's own. They differ on every
     Shopify Markets sale outside the US, which is what decides whether a figure
     denominated in the shop currency — the budget cap above all — may be
     compared against a price on the page at all. With nothing printed, the two
     are read as equal: a single-market store is the safe reading, and it is
     what every caller assumed before these fields existed. */
  function iso3(v) {
    return (typeof v === 'string' && /^[A-Za-z]{3}$/.test(v)) ? v.toUpperCase() : null;
  }

  function activeCurrency() {
    try {
      var c = w.Shopify && w.Shopify.currency && w.Shopify.currency.active;
      return iso3(c) || 'USD';
    } catch (e) { return 'USD'; }
  }

  function fromPage() {
    var el = d.getElementById('sts-customer-data');
    var data = el ? parse(el.textContent) : null;
    return data && typeof data === 'object' ? data : null;
  }

  function build() {
    var s = STS.state || {};
    var guest = parse(lsGet(GUEST_KEY));
    var deviceBrands = handles(guest && guest.brands);
    var page = fromPage();

    if (page) {
      var cents = parseInt(page.budgetCents, 10);
      var brands = handles(page.likedBrands);
      return {
        v: 1,
        source: 'liquid',
        loggedIn: true,
        type: typeCode(page.type) || typeCode(s.type),
        typedAt: iso(page.typedAt) || iso(s.typedAt),
        likedBrands: brands.length ? brands : deviceBrands,
        budget: typeof page.budget === 'string' && page.budget ? page.budget : null,
        budgetCents: cents > 0 ? cents : 0,
        currency: iso3(page.currency) || activeCurrency(),
        shopCurrency: iso3(page.shopCurrency) || activeCurrency()
      };
    }

    return {
      v: 1,
      source: 'local',
      loggedIn: !!s.loggedIn,
      type: typeCode(s.type),
      typedAt: iso(s.typedAt),
      likedBrands: deviceBrands,
      budget: null,
      budgetCents: 0,
      currency: activeCurrency(),
      shopCurrency: activeCurrency()
    };
  }

  var base = build();

  /* Adds what is missing and leaves every existing field alone. Arrays are copied so a
     consumer that mutates its list cannot change what the next fill hands out. */
  function fill(target) {
    for (var k in base) {
      if (own(base, k) && target[k] === undefined) {
        target[k] = Array.isArray(base[k]) ? base[k].slice(0) : base[k];
      }
    }
    return target;
  }

  STS.profile = fill(STS.profile && typeof STS.profile === 'object' ? STS.profile : {});

  /* The Skin Profile replaces STS.profile with its own object on every save. Registered
     before any consumer's listener, so consumers see the gaps already filled. */
  d.addEventListener('sts:profile:change', function () {
    if (STS.profile && typeof STS.profile === 'object') fill(STS.profile);
  });

  // ----------------------------------------------------------------- ready

  var fired = false;

  function ready() {
    if (fired) return;
    fired = true;
    STS.profileReady = true;
    d.dispatchEvent(new CustomEvent('sts:profile:ready', { detail: STS.profile }));
  }

  STS.onProfile = function (fn) {
    if (typeof fn !== 'function') return;
    function run() { fn(STS.profile); }
    if (fired) run();
    else d.addEventListener('sts:profile:ready', run);
    d.addEventListener('sts:profile:change', run);
  };

  /* A deferred script runs while readyState is already 'interactive', before
     DOMContentLoaded, so 'loading' is the wrong test here. `load` is the fallback for
     a copy injected after DOMContentLoaded; ready() only ever fires once. */
  if (d.readyState === 'complete') {
    ready();
  } else {
    d.addEventListener('DOMContentLoaded', ready);
    w.addEventListener('load', ready);
  }
}(window, document));
